import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Activity, ArrowRight } from 'lucide-react';

import { playMcsaTone } from '../../../../helpers/mcsa-sfx.helper';
import type { TMcsaTone } from '../../../../helpers/mcsa-sfx.helper';

interface ShapeFocusProps {
    onComplete: () => void;
    muted?: boolean;
}

type TScreen = 'intro' | 'countdown' | 'running' | 'practiceDone' | 'results';
type TMode = 'practice' | 'test';
type TFeedback = 'idle' | 'good' | 'bad';
type TShape = 'o' | 'd' | 'b';

interface IPending {
    onset: number;
    answered: boolean;
    at: number;
}

interface IHit {
    rt: number;
    at: number;
}

interface IMetrics {
    meanRT: number;
    lapses: number;
    targets: number;
    hits: number;
    hitRate: number;
    misses: number;
    falseAlarms: number;
    thirds: number[];
}

const DURATION_SEC = 180;
const PRACTICE_SEC = 25;
const FLICKER_MS = 500;
const RESPONSE_WINDOW = 1000;
const MIN_GAP_MS = 2000;
const MAX_GAP_MS = 10000;

const mean = (values: number[]) => (values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0);

const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return `${mins}:${String(rest).padStart(2, '0')}`;
};

const randDistractor = (): TShape => (Math.random() < 0.5 ? 'd' : 'b');

const randGapSlots = () => {
    const ms = MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);
    return Math.max(4, Math.round(ms / FLICKER_MS));
};

const computeMetrics = (hits: IHit[], targets: number, misses: number, falseAlarms: number): IMetrics => {
    const rts = hits.map(hit => hit.rt).sort((a, b) => a - b);
    const meanRT = Math.round(mean(rts));
    const lapses = rts.filter(rt => rt > 500).length;
    const hitRate = targets ? Math.round((hits.length / targets) * 100) : 0;
    const totalMs = DURATION_SEC * 1000;
    const thirds: number[][] = [[], [], []];
    hits.forEach(hit => {
        const index = Math.min(2, Math.floor((hit.at / totalMs) * 3));
        thirds[index].push(hit.rt);
    });
    return {
        meanRT,
        lapses,
        targets,
        hits: hits.length,
        hitRate,
        misses,
        falseAlarms,
        thirds: thirds.map(group => Math.round(mean(group))),
    };
};

const verdictFor = (metrics: IMetrics | null) => {
    if (!metrics || metrics.hits < 3) {
        return {
            head: 'Not enough data',
            body: 'The run was too short to score reliably. Give the full 3-minute test a go for a real read on your vigilance.',
        };
    }
    const problems: string[] = [];
    if (metrics.lapses >= 5) problems.push('several attention lapses');
    if (metrics.falseAlarms >= 4) problems.push('some impulsive taps');
    if (metrics.misses >= 4) problems.push('a few missed targets');
    const decrement = metrics.thirds[2] && metrics.thirds[0] ? metrics.thirds[2] - metrics.thirds[0] : 0;
    if (metrics.meanRT < 380 && metrics.lapses <= 2 && metrics.falseAlarms <= 2) {
        return {
            head: 'Sharp and steady',
            body: 'Fast reactions, tight impulse control, and almost no lapses — you held focus across the whole run. This is exactly the sustained vigilance the test looks for.',
        };
    }
    if (metrics.meanRT < 450 && metrics.lapses <= 4) {
        const body = decrement > 60
            ? 'Solid reaction speed with focus that mostly held. Your speed drifted slower toward the end — a classic sign of fatigue on task.'
            : 'Solid reaction speed with focus that mostly held. You stayed fairly consistent from start to finish.';
        return { head: 'Focused, with room to sharpen', body };
    }
    const extra = problems.length ? `, with ${problems.join(', ')}.` : '.';
    const body = decrement > 60
        ? `Your responses were on the slower side${extra} Vigilance clearly dropped as the test went on.`
        : `Your responses were on the slower side${extra} Worth retrying when rested — fatigue and distraction both show up strongly here.`;
    return { head: 'Vigilance ran thin', body };
};

export const ShapeFocus = ({ onComplete, muted = false }: ShapeFocusProps) => {
    const [screen, setScreen] = useState<TScreen>('intro');
    const [mode, setMode] = useState<TMode>('test');
    const [currentShape, setCurrentShape] = useState<TShape>('d');
    const [shapeKey, setShapeKey] = useState(0);
    const [countdown, setCountdown] = useState(3);
    const [timeLeft, setTimeLeft] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [feedback, setFeedback] = useState<TFeedback>('idle');
    const [practiceHits, setPracticeHits] = useState(0);
    const [practiceTargets, setPracticeTargets] = useState(0);
    const [metrics, setMetrics] = useState<IMetrics | null>(null);

    const pendingRef = useRef<IPending | null>(null);
    const responsesRef = useRef<IHit[]>([]);
    const missesRef = useRef(0);
    const falseAlarmsRef = useRef(0);
    const falseStartsRef = useRef(0);
    const targetsRef = useRef(0);
    const slotsLeftRef = useRef(0);
    const sessionStartRef = useRef(0);
    const tickRef = useRef<number | null>(null);
    const clockRef = useRef<number | null>(null);
    const countdownRef = useRef<number | null>(null);
    const feedbackRef = useRef<number | null>(null);
    const mutedRef = useRef(muted);
    const modeRef = useRef<TMode>(mode);

    mutedRef.current = muted;
    modeRef.current = mode;

    const clearTimers = useCallback(() => {
        if (tickRef.current) window.clearInterval(tickRef.current);
        if (clockRef.current) window.clearInterval(clockRef.current);
        if (countdownRef.current) window.clearInterval(countdownRef.current);
        if (feedbackRef.current) window.clearTimeout(feedbackRef.current);
        tickRef.current = clockRef.current = countdownRef.current = null;
    }, []);

    const flash = useCallback((kind: TFeedback) => {
        setFeedback(kind);
        if (feedbackRef.current) window.clearTimeout(feedbackRef.current);
        feedbackRef.current = window.setTimeout(() => setFeedback('idle'), 240);
    }, []);

    const tone = useCallback((kind: TMcsaTone) => {
        if (mutedRef.current) return;
        playMcsaTone(kind);
    }, []);

    const doTick = useCallback(() => {
        const now = performance.now();
        if (pendingRef.current && !pendingRef.current.answered && (now - pendingRef.current.onset) > RESPONSE_WINDOW) {
            missesRef.current += 1;
            pendingRef.current = null;
        }
        slotsLeftRef.current -= 1;
        let shape: TShape;
        if (slotsLeftRef.current <= 0 && !pendingRef.current) {
            shape = 'o';
            pendingRef.current = { onset: now, answered: false, at: now - sessionStartRef.current };
            targetsRef.current += 1;
            slotsLeftRef.current = randGapSlots();
            if (modeRef.current === 'practice') setPracticeTargets(current => current + 1);
        } else {
            shape = randDistractor();
        }
        setCurrentShape(shape);
        setShapeKey(current => current + 1);
    }, []);

    const startLoop = useCallback((nextMode: TMode) => {
        const totalMs = (nextMode === 'practice' ? PRACTICE_SEC : DURATION_SEC) * 1000;
        sessionStartRef.current = performance.now();
        slotsLeftRef.current = randGapSlots();
        pendingRef.current = null;
        setScreen('running');
        setMode(nextMode);
        setTimeLeft(Math.round(totalMs / 1000));
        setElapsed(0);
        setCurrentShape(randDistractor());
        setShapeKey(current => current + 1);
        doTick();
        tickRef.current = window.setInterval(doTick, FLICKER_MS);
        clockRef.current = window.setInterval(() => {
            const elapsedMs = performance.now() - sessionStartRef.current;
            setElapsed(elapsedMs);
            setTimeLeft(Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000)));
            if (elapsedMs >= totalMs) {
                clearTimers();
                if (nextMode === 'practice') setScreen('practiceDone');
                else setMetrics(computeMetrics(responsesRef.current, targetsRef.current, missesRef.current, falseAlarmsRef.current));
                if (nextMode !== 'practice') setScreen('results');
            }
        }, 200);
    }, [clearTimers, doTick]);

    const beginCountdown = useCallback((nextMode: TMode) => {
        clearTimers();
        pendingRef.current = null;
        responsesRef.current = [];
        missesRef.current = 0;
        falseAlarmsRef.current = 0;
        falseStartsRef.current = 0;
        targetsRef.current = 0;
        setMode(nextMode);
        setCountdown(3);
        setFeedback('idle');
        setPracticeHits(0);
        setPracticeTargets(0);
        setMetrics(null);
        setScreen('countdown');
        countdownRef.current = window.setInterval(() => {
            setCountdown(current => {
                if (current <= 1) {
                    if (countdownRef.current) window.clearInterval(countdownRef.current);
                    countdownRef.current = null;
                    startLoop(nextMode);
                    return 0;
                }
                return current - 1;
            });
        }, 800);
    }, [clearTimers, startLoop]);

    const registerPress = useCallback(() => {
        const now = performance.now();
        const pending = pendingRef.current;
        if (pending && !pending.answered) {
            const rt = now - pending.onset;
            if (rt < 100) {
                falseStartsRef.current += 1;
                pending.answered = true;
                pendingRef.current = null;
                flash('bad');
                tone('bad');
                return;
            }
            if (rt <= RESPONSE_WINDOW) {
                responsesRef.current.push({ rt, at: pending.at });
                pending.answered = true;
                pendingRef.current = null;
                if (modeRef.current === 'practice') setPracticeHits(current => current + 1);
                flash('good');
                tone('good');
            }
            return;
        }
        falseAlarmsRef.current += 1;
        flash('bad');
        tone('bad');
    }, [flash, tone]);

    const abort = useCallback(() => {
        clearTimers();
        if (modeRef.current === 'practice') {
            setScreen('practiceDone');
            return;
        }
        setMetrics(computeMetrics(responsesRef.current, targetsRef.current, missesRef.current, falseAlarmsRef.current));
        setScreen('results');
    }, [clearTimers]);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.code !== 'Space' && event.key !== ' ') return;
            event.preventDefault();
            if (screen === 'running') registerPress();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [screen, registerPress]);

    useEffect(() => () => clearTimers(), [clearTimers]);

    const totalMs = mode === 'practice' ? PRACTICE_SEC * 1000 : DURATION_SEC * 1000;
    const progressPct = Math.min(100, (elapsed / totalMs) * 100);
    const lowTime = timeLeft <= 15 && mode === 'test' && screen === 'running';
    const verdict = verdictFor(metrics);
    const lapseColor = metrics && metrics.lapses >= 5 ? '#EF4444' : metrics && metrics.lapses >= 2 ? '#F59E0B' : '#4A306D';

    if (screen === 'intro') {
        return (
            <div className='mcsa_game__shape_focus mcsa_game__shape_focus--center'>
                <div className='mcsa_game__shape_focus__card'>
                    <h1 className='mcsa_game__shape_focus__title'>ShapeFocus</h1>
                    <p className='mcsa_game__shape_focus__lead'>
                        <b>Goal:</b> Get as many 'o' as you can!<br />
                        <b>Time:</b> 3 minutes
                    </p>
                    <div className='mcsa_game__shape_focus__howto'>
                        <p className='mcsa_game__shape_focus__howto_label'>Instructions</p>
                        <div className='mcsa_game__shape_focus__howto_row'>
                            <span className='mcsa_game__shape_focus__glyph'>o</span>
                            <p>
                                You will see one of 3 letters flash on the screen.
                                Ignore all <b>d</b> and <b>b</b> but when you see an <b>o</b>, press <b>SPACE BAR</b> as fast as you can.
                            </p>
                        </div>
                        <p className='mcsa_game__shape_focus__hint_dot'>Speed counts so respond the instant you see it.</p>
                    </div>
                    <div className='mcsa_game__shape_focus__actions'>
                        <button type='button' className='mcsa_game__shape_focus__cta' onClick={() => beginCountdown('practice')}>
                            Warm up with a practice round
                        </button>
                        <button type='button' className='mcsa_game__shape_focus__text_btn' onClick={() => beginCountdown('test')}>
                            Skip to the real test →
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (screen === 'countdown') {
        return (
            <div className='mcsa_game__shape_focus mcsa_game__shape_focus--center'>
                <p className='mcsa_game__shape_focus__countdown_label'>
                    {mode === 'practice' ? 'Practice starts in…' : 'Get ready…'}
                </p>
                <div key={countdown} className='mcsa_game__shape_focus__countdown'>{countdown}</div>
                <p className='mcsa_game__shape_focus__caption'>Fingers on the <b>SPACE</b> bar</p>
            </div>
        );
    }

    if (screen === 'practiceDone') {
        return (
            <div className='mcsa_game__shape_focus mcsa_game__shape_focus--center'>
                <div className='mcsa_game__shape_focus__card mcsa_game__shape_focus__card--narrow'>
                    <div className='mcsa_game__shape_focus__badge'>o</div>
                    <h2 className='mcsa_game__shape_focus__heading'>Nice warm-up.</h2>
                    <p className='mcsa_game__shape_focus__body'>
                        You caught {practiceHits} of {practiceTargets} target{practiceTargets === 1 ? '' : 's'}. You've got the rhythm — the real 3-minute test scores everything, so settle in and stay sharp the whole way through.
                    </p>
                    <div className='mcsa_game__shape_focus__actions'>
                        <button type='button' className='mcsa_game__shape_focus__cta' onClick={() => beginCountdown('test')}>
                            Start the real test
                        </button>
                        <button type='button' className='mcsa_game__shape_focus__ghost' onClick={() => beginCountdown('practice')}>
                            Practice again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (screen === 'results') {
        return (
            <div className='mcsa_game__shape_focus mcsa_game__shape_focus--center'>
                <div className='mcsa_game__shape_focus__card mcsa_game__shape_focus__card--narrow'>
                    <div className='mcsa_game__shape_focus__result_icon'>
                        <Activity size={30} />
                    </div>
                    <h2 className='mcsa_game__shape_focus__heading'>{verdict.head}</h2>
                    <p className='mcsa_game__shape_focus__body'>{verdict.body}</p>
                    <div className='mcsa_game__shape_focus__stats'>
                        <div>
                            <strong>{metrics ? `${metrics.meanRT}ms` : '—'}</strong>
                            <span>avg reaction</span>
                        </div>
                        <div>
                            <strong>{metrics ? `${metrics.hitRate}%` : '—'}</strong>
                            <span>targets caught</span>
                        </div>
                        <div>
                            <strong style={{ color: lapseColor }}>{metrics ? String(metrics.lapses) : '—'}</strong>
                            <span>lapses</span>
                        </div>
                    </div>
                    <p className='mcsa_game__shape_focus__lock'>🔒 Full cognitive security report will be available at the end!</p>
                    <div className='mcsa_game__shape_focus__actions'>
                        <button type='button' className='mcsa_game__shape_focus__cta' onClick={onComplete}>
                            Continue the journey <ArrowRight size={18} />
                        </button>
                        <button type='button' className='mcsa_game__shape_focus__ghost' onClick={() => { clearTimers(); setScreen('intro'); }}>
                            Play again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='mcsa_game__shape_focus mcsa_game__shape_focus--play'>
            <div className='mcsa_game__shape_focus__top'>
                <div>
                    {mode === 'practice' && (
                        <span className='mcsa_game__shape_focus__practice'>Practice round · not scored</span>
                    )}
                </div>
                <span className={`mcsa_game__shape_focus__timer${lowTime ? ' mcsa_game__shape_focus__timer--low' : ''}`}>
                    {formatTime(timeLeft)}
                </span>
            </div>
            <div className='mcsa_game__shape_focus__track'>
                <div className='mcsa_game__shape_focus__fill' style={{ width: `${progressPct}%` }} />
            </div>
            <button
                type='button'
                className={`mcsa_game__shape_focus__stage mcsa_game__shape_focus__stage--${feedback}`}
                onClick={registerPress}
                aria-label='Press when you see o'
            >
                <span className='mcsa_game__shape_focus__dots' aria-hidden='true' />
                <span key={shapeKey} className='mcsa_game__shape_focus__letter'>{currentShape}</span>
            </button>
            <p className='mcsa_game__shape_focus__play_hint'>
                Press <b>SPACE</b> the instant you see <span>o</span>
            </p>
            <button type='button' className='mcsa_game__shape_focus__abort' onClick={abort}>End early</button>
        </div>
    );
};
